import Phaser from 'phaser';
import { getLevel } from '../levels';
import { codenameOf, getChapterForLevel } from '../levels/manifest';
import { registerOverlay } from '../back-button';
import { SANS, Panel, uiButton, clean, reducedMotion } from '../render/theme';
import { t, FONT_STACK } from '../i18n';
import { GraphView } from '../render/GraphView';
import { Backdrop } from '../render/Backdrop';
import { CameraRig } from '../render/CameraRig';
import { Legend } from '../render/Legend';
import { NodeIntroPopup } from '../render/NodeIntroPopup';
import { WireIntroPopup } from '../render/WireIntroPopup';
import { drawWatermark } from '../render/watermark';
import { Sfx } from '../audio/sfx';
import { simulate } from '../sim/simulator';
import { isBreachable, type NodeType, type SimResult, type WireType } from '../sim/types';
import { isProceduralId, proceduralIndex, isDailyId, dailyKeyFromId, dailyKeyYesterday } from '../levels/procgen';
import { Progress, MAX_LIVES } from '../progress';
import { Ads } from '../ads';
import { hintCandidates } from '../hint';
import { scoreLevel, fmtScore } from '../score';
import { Buzz } from '../haptics';
import type { LevelDef } from '../sim/types';

export class GameScene extends Phaser.Scene {
  /** Android back button action, read by back-button.ts. */
  onBack?: () => void;
  private graph?: GraphView;
  private sfx!: Sfx;
  private levelId!: string;
  private chapterId?: string;
  private hud?: Phaser.GameObjects.Container;
  /** Bottom edge of the top HUD block, so floating hints sit below it. */
  private hudBottom = 330;
  private tapHint?: Phaser.GameObjects.Text;
  private tutorial?: Phaser.GameObjects.Container;
  private tapped = false;
  private rig?: CameraRig;
  private legend?: Legend;
  private backdrop?: Backdrop;
  /** UI-camera objects — these are zoom-immune and viewport-locked. */
  private uiObjects: Phaser.GameObjects.GameObject[] = [];
  private uiCam?: Phaser.Cameras.Scene2D.Camera;
  /** While true, dynamically-added GameObjects are routed to world cam only. */
  private inPlayback = false;
  /** Endless-only HUD bits + cached unique winner for the hint feature. */
  private hintBtn?: Phaser.GameObjects.Container;
  private livesText?: Phaser.GameObjects.Text;
  private hintBusy = false;
  /** Endless scoring: when the level opened, whether a hint was used, think time. */
  private levelStartMs = 0;
  private hintUsedThisLevel = false;
  private lastThinkMs = 0;

  constructor() {
    super('Game');
  }

  init(data: { levelId: string; chapterId?: string }) {
    this.levelId = data.levelId;
    this.chapterId = data.chapterId ?? getChapterForLevel(data.levelId)?.id;
    this.tapped = false;
    this.uiObjects = [];
    this.levelStartMs = Date.now();
    this.hintUsedThisLevel = false;
    this.lastThinkMs = 0;
  }

  create() {
    // Per-tier mood tinting — the bg subtly shifts with difficulty so a player
    // walking into a NIGHTMARE level can feel the air change before tapping.
    const tier = getLevel(this.levelId)?.tier;
    const tintFor = (t?: string): { glow: number; spread: number } => {
      switch (t) {
        case 'tutorial':   return { glow: 0x0d1a2e, spread: 0x0a1430 };
        case 'beginner':   return { glow: 0x0d1a2e, spread: 0x0a1430 };
        case 'easy':       return { glow: 0x0e1a2c, spread: 0x0a1c34 };
        case 'medium':     return { glow: 0x1e1a18, spread: 0x261a08 };
        case 'hard':       return { glow: 0x261830, spread: 0x1a0a26 };
        case 'expert':     return { glow: 0x2a0a26, spread: 0x1d0420 };
        case 'brutal':     return { glow: 0x2c0918, spread: 0x1a0410 };
        case 'nightmare':  return { glow: 0x1f0838, spread: 0x12042a };
        default:           return { glow: 0x0d0e1a, spread: 0x0a1430 };
      }
    };
    const tt = tintFor(tier);
    this.backdrop = new Backdrop(this, {
      glow: 0.45, scanlines: true, particles: true,
      glowTint: tt.glow, spreadTint: tt.spread,
    });

    const level = getLevel(this.levelId);
    if (!level) {
      this.add.text(360, 640, `level ${this.levelId} not found`, {
        fontFamily: 'monospace', fontSize: '20px', color: '#ff5577',
      }).setOrigin(0.5);
      return;
    }

    this.sfx = (this.registry.get('sfx') as Sfx | undefined) ?? new Sfx();
    this.registry.set('sfx', this.sfx);
    this.sfx.init();

    const minX = Math.min(...level.nodes.map((n) => n.x)) - 120;
    const maxX = Math.max(...level.nodes.map((n) => n.x)) + 120;
    const minY = Math.min(...level.nodes.map((n) => n.y)) - 120;
    const maxY = Math.max(...level.nodes.map((n) => n.y)) + 120;
    this.rig = new CameraRig(this, { minX, minY, maxX, maxY });

    this.buildHud(codenameOf(this.levelId), level.intro ?? '', level.hint ?? '');

    this.graph = new GraphView(this, level, this.sfx, this.backdrop);
    this.graph.onNodeTap((nodeId) => this.onBreach(nodeId));

    const types = Array.from(new Set(level.nodes.map((n) => n.type)));
    this.legend = new Legend(this, types, (panel) => {
      // Lazily-created panel must be routed to UI cam, ignored by main cam.
      this.cameras.main.ignore(panel);
    });
    this.uiObjects.push(...this.legend.uiRoots());

    this.rig.frame({ minX, minY, maxX, maxY }, { top: this.hudBottom + 20, bottom: isProceduralId(this.levelId) ? 300 : 160 });

    // Recenter control — only for levels big enough to pan/zoom around. Snaps
    // the camera back to frame the whole board after the player has explored it.
    if (this.rig.canPan()) {
      const { width: sw, height: sh } = this.scale;
      const recenter = this.addUi(this.add.text(sw - 42, sh - 190, '⊕ recenter', {
        fontFamily: SANS, fontSize: '26px', color: '#88a8ff', fontStyle: '700',
        padding: { x: 16, y: 22 },
      }).setOrigin(1, 0.5).setScrollFactor(0).setInteractive({ useHandCursor: true }));
      recenter.on('pointerover', () => recenter.setColor('#eaf2ff'));
      recenter.on('pointerout', () => recenter.setColor('#88a8ff'));
      recenter.on('pointerdown', () => { this.sfx.ui(); this.rig?.recenter(); });
    }

    // Endless-run HUD: lives readout + ad-hint button. Built before the UI
    // camera split so addUi() routes them to the HUD camera.
    if (isProceduralId(this.levelId)) this.buildEndlessHud(level);

    // === UI camera ===
    // Dedicated camera for HUD/legend/tutorial-panel so they don't zoom
    // or scroll with the main game camera. We configure both cameras to
    // ignore each other's objects.
    const { width, height } = this.scale;
    this.uiCam = this.cameras.add(0, 0, width, height);
    this.uiCam.setName('ui');
    this.uiCam.setScroll(0, 0);

    // Split scene children into world vs UI by membership in uiObjects.
    // Containers carry their children with them, so we only have to handle
    // direct scene-list members.
    const uiSet = new Set(this.uiObjects);
    const allChildren = this.children.list as Phaser.GameObjects.GameObject[];
    const worldObjects = allChildren.filter((o) => !uiSet.has(o));

    this.uiCam.ignore(worldObjects);
    this.cameras.main.ignore(this.uiObjects);

    // While a cascade is playing, every newly-created GameObject (pulse
    // dots, collapse shockwaves, shrapnel sparks) is a world object. Route
    // them out of the UI camera so we don't get a ghost double-animation.
    // We only act DURING playback so things like the lazily-created
    // legend panel — opened outside of playback — aren't auto-routed.
    //
    // NOTE: registered with a stable method ref and removed in shutdown().
    // Scene.events listeners are NOT auto-cleared on scene restart, so an
    // inline closure here would accumulate one extra listener per level
    // played — after a dozen levels every object creation fires a dozen
    // callbacks, which is exactly the "freezes a lot during rapid play" bug.
    this.events.on(Phaser.Scenes.Events.ADDED_TO_SCENE, this.routeNewObject, this);

    // Phaser does NOT auto-invoke a Scene's shutdown() method — it only emits
    // the SHUTDOWN event, and it does NOT clear custom this.events listeners on
    // restart. So we wire cleanup to the event explicitly (once per lifecycle).
    // Without this, every level played leaks its event listeners and the game
    // grinds to a freeze after a dozen levels.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);

    this.cameras.main.flash(180, 0, 0, 0);

    // Watermark — UI-only (main cam ignores, UI cam renders)
    const wmObjects = drawWatermark(this);
    this.uiObjects.push(...wmObjects);
    for (const o of wmObjects) this.cameras.main.ignore(o);

    // === Tutorial gates ===
    // Block input until any new-node-type/wire-type intro popups have been
    // shown and the FTUE objective card (level 1) is dismissed.
    this.graph.setAllInteractive(false);
    const nodeTypes = level.nodes.map((n) => n.type);
    const wireTypes = level.wires.map((w) => w.type ?? 'normal');
    let inputReopened = false;
    const reopenInput = () => {
      if (inputReopened) return;
      inputReopened = true;
      // Belt + suspenders: re-enable scene-level input, then re-enable each
      // node's hit zone. A stuck pointer state on the scene plugin would
      // otherwise look identical to "frozen input" from the player's POV.
      this.input.enabled = true;
      this.graph?.setAllInteractive(true);
      if (level.tier === 'tutorial') {
        try { this.buildTutorial(); } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[nodefall] buildTutorial failed:', err);
        }
      }
    };

    // Deadman switch — if onboarding hangs for any reason, force-unlock input
    // after 30 seconds so the player is never permanently frozen. Cleared if
    // the chain resolves first.
    const deadman = this.time.delayedCall(30_000, () => {
      // eslint-disable-next-line no-console
      console.warn('[nodefall] onboarding deadman fired — forcing input unlock');
      reopenInput();
    });

    this.runOnboarding(nodeTypes, wireTypes, level.tier === 'tutorial')
      .then(() => { deadman.remove(false); reopenInput(); })
      .catch((err) => {
        deadman.remove(false);
        // eslint-disable-next-line no-console
        console.error('[nodefall] onboarding failed:', err);
        reopenInput();
      });
  }

  /**
   * Shows, in sequence:
   *   1. The big "what is the objective" card on the very first run.
   *   2. One NodeIntroPopup per unseen node type present in this level.
   *   3. One WireIntroPopup per unseen wire type present in this level.
   * All three gates write to Progress so they only fire once per player.
   */
  private async runOnboarding(typesPresent: NodeType[], wiresPresent: WireType[], isFirstLevel: boolean) {
    const nodeOrder: NodeType[] = [
      'normal', 'required', 'protected', 'relay', 'shield', 'splitter', 'capacitor', 'core',
    ];
    const uniqueNodes = new Set(typesPresent);
    const nodeQueue: NodeType[] = nodeOrder.filter((t) => uniqueNodes.has(t) && !Progress.hasSeenNode(t));

    const wireOrder: WireType[] = ['directional', 'delay', 'locked', 'forbidden'];
    const uniqueWires = new Set(wiresPresent);
    const wireQueue: WireType[] = wireOrder.filter((t) => uniqueWires.has(t) && !Progress.hasSeenWire(t));

    if (isFirstLevel && Progress.isFirstRun()) {
      try {
        await this.showFirstRunCard();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[nodefall] showFirstRunCard failed:', err);
      }
      Progress.clearFirstRun();
    }

    for (const t of nodeQueue) {
      try {
        await NodeIntroPopup.show(this, t, this.cameras.main);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[nodefall] node popup '${t}' failed:`, err);
      }
      Progress.markNodeSeen(t);
    }

    for (const t of wireQueue) {
      try {
        await WireIntroPopup.show(this, t, this.cameras.main);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[nodefall] wire popup '${t}' failed:`, err);
      }
      Progress.markWireSeen(t);
    }

    // After ANY popups ran, briefly pulse the "?" button so the player
    // learns the legend is always available.
    if ((nodeQueue.length + wireQueue.length) > 0 && this.legend) {
      this.legend.pulseHint(this);
    }
  }

  /**
   * First-ever-launch card. Explains the one rule of the game before the
   * player has touched anything.
   */
  private showFirstRunCard(): Promise<void> {
    return new Promise((resolve) => {
      const { width, height } = this.scale;

      const dim = this.add.rectangle(0, 0, width, height, 0x000000, 0)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(1650)
        .setInteractive();
      this.tweens.add({ targets: dim, alpha: 0.85, duration: 200 });
      dim.on('pointerdown', () => close());

      const root = this.add.container(0, 0).setDepth(1700).setScrollFactor(0);

      const cardW = Math.min(width - 90, 840);
      const cardH = 940;
      const cx = width / 2;
      const cy = height / 2;
      const card = new Panel(this, cx - cardW / 2, cy - cardH / 2, cardW, cardH, {
        fill: 0x0b1226, fillAlpha: 0.99, stroke: 0x18e0ff, strokeAlpha: 0.85,
      }).gfx;
      root.add(card);

      const bx = cx - cardW / 2;
      const by = cy - cardH / 2;

      const eyebrow = this.add.text(cx, by + 39, clean(t('brief_eyebrow')), {
        fontFamily: SANS,
        fontSize: '24px',
        color: '#8d98bd',
        fontStyle: '800',
      }).setOrigin(0.5);
      root.add(eyebrow);

      const title = this.add.text(cx, by + 96, t('brief_title'), {
        fontFamily: SANS,
        fontSize: '64px',
        color: '#eaf2ff',
        fontStyle: '800',
        align: 'center',
        lineSpacing: 9,
      }).setOrigin(0.5, 0);
      root.add(title);

      

      const lines = [
        clean(t('brief_line_1')),
        clean(t('brief_line_2')),
        clean(t('brief_line_3')),
        clean(t('brief_line_4')),
      ];
      let ly = by + 300;
      for (const ln of lines) {
        const lineTxt = this.add.text(bx + 54, ly, ln, {
          fontFamily: SANS,
          fontSize: '32px',
          color: '#cdd8f5',
          wordWrap: { width: cardW - 108 },
          lineSpacing: 6,
        }).setOrigin(0, 0);
        root.add(lineTxt);
        ly += Math.max(64, lineTxt.height + 22);
      }

      const btnY = by + cardH - 100;
      const btnC = uiButton(this, cx, btnY, cardW - 120, 124, clean(t('brief_begin')), 0x18e0ff, () => close(), { primary: true, size: 36 });
      root.add(btnC);
      const btn = btnC.list[2] as Phaser.GameObjects.Rectangle;

      // Route popup to UI camera only
      this.cameras.main.ignore(root);
      this.cameras.main.ignore(dim);

      if (!reducedMotion()) {
        root.setAlpha(0);
        this.tweens.add({ targets: root, alpha: 1, duration: 220 });
        card.setScale(0.94);
        this.tweens.add({ targets: card, scale: 1, duration: 260, ease: 'Cubic.easeOut' });
      }

      let closing = false;
      const mountedAt = this.time.now;
      const MIN_VISIBLE_MS = 150;
      const close = () => {
        if (closing) return;
        if (this.time.now - mountedAt < MIN_VISIBLE_MS) return;
        closing = true;
        btn.disableInteractive();
        dim.disableInteractive();
        this.tweens.add({
          targets: [root, dim],
          alpha: 0,
          duration: 200,
          onComplete: () => { root.destroy(); dim.destroy(); resolve(); },
        });
      };
    });
  }

  private addUi<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    this.uiObjects.push(obj);
    return obj;
  }

  private buildHud(name: string, intro: string, hint: string) {
    const { width, height } = this.scale;
    this.hud = this.add.container(0, 0).setDepth(900).setScrollFactor(0);
    this.addUi(this.hud);

    const back = this.addUi(this.add.text(42, 58, `‹  ${t('levels')}`, {
      fontFamily: SANS, fontSize: '30px', color: '#8fb0ff', fontStyle: '700',
    }).setScrollFactor(0));
    // Hit area is a generous box around the label, not just the glyphs.
    const backHit = this.addUi(this.add.rectangle(130, 72, 300, 132, 0xffffff, 0)
      .setScrollFactor(0).setInteractive({ useHandCursor: true }));
    backHit.on('pointerover', () => { back.setColor('#ffffff'); this.sfx.ui(); });
    backHit.on('pointerout', () => back.setColor('#8fb0ff'));
    const leave = () => {
      if (this.chapterId) this.scene.start('Chapter', { chapterId: this.chapterId });
      else this.scene.start('Menu');
    };
    this.onBack = () => { this.sfx.tap(); leave(); };
    backHit.on('pointerdown', () => { this.sfx.tap(); leave(); });

    // Title sits on its own row below the controls so it never meets them.
    const title = this.addUi(this.add.text(width / 2, 172, name.toUpperCase(), {
      fontFamily: SANS, fontSize: '60px', color: '#f2f6ff', fontStyle: '800',
    }).setOrigin(0.5).setScrollFactor(0));

    const introTxt = this.addUi(this.add.text(width / 2, 224, intro, {
      fontFamily: SANS, fontSize: '30px', color: '#cdd8f5',
      wordWrap: { width: width - 150 }, align: 'center',
    }).setOrigin(0.5, 0).setScrollFactor(0));

    let lastY = introTxt.y + introTxt.height;
    this.hudBottom = lastY + 20;
    if (hint) {
      const hintText = this.addUi(this.add.text(width / 2, lastY + 14, hint, {
        fontFamily: SANS, fontSize: '26px', color: '#8d98bd',
        wordWrap: { width: width - 150 }, align: 'center',
      }).setOrigin(0.5, 0).setScrollFactor(0));
      lastY = hintText.y + hintText.height;
    }

    // === Live objective chip ===
    const level = getLevel(this.levelId);
    if (level) {
      const reqTotal = level.objective.requiredNodeIds.length;
      const coreId = level.objective.coreNodeId;
      const chipY = lastY + 46;
      this.hudBottom = chipY + 40;
      const parts: string[] = [];
      if (reqTotal > 0) parts.push(`${t('hud_required')} ${String(0).padStart(2, '0')} / ${String(reqTotal).padStart(2, '0')}`);
      if (coreId) parts.push(t('hud_core_online'));
      const chipText = parts.join('  ·  ');
      if (chipText) {
        const chipBg = this.addUi(
          this.add.rectangle(width / 2, chipY, 480, 52, 0x0b1226, 0.9)
            .setStrokeStyle(2, 0x6a8cff, 0.6)
            .setScrollFactor(0)
        );
        const chipLabel = this.addUi(
          this.add.text(width / 2, chipY, chipText, {
            fontFamily: SANS, fontSize: '26px',
            color: '#88a8ff', fontStyle: '700',
          }).setOrigin(0.5).setScrollFactor(0)
        );
        chipBg.setSize(chipLabel.width + 56, 52);

        this.events.on('nodefall:required-collapsed', (d: { remaining: number; total: number }) => {
          const reqLine = `${t('hud_required')} ${String(d.total - d.remaining).padStart(2, '0')} / ${String(d.total).padStart(2, '0')}`;
          const next = [reqLine];
          if (coreId) next.push(t('hud_core_online'));
          chipLabel.setText(next.join('  ·  '));
          chipLabel.setColor(d.remaining === 0 ? '#88ffb0' : '#88a8ff');
          chipBg.setSize(chipLabel.width + 56, 52);
          this.tweens.add({
            targets: [chipBg, chipLabel], scale: { from: 1.2, to: 1 },
            duration: 200, ease: 'Cubic.easeOut',
          });
        });
        this.events.on('nodefall:core-collapsed', () => {
          const reqLine = reqTotal > 0 ? chipLabel.text.split('  ·  ')[0] : '';
          const parts2 = [reqLine, t('hud_core_down')].filter(Boolean);
          chipLabel.setText(parts2.join('  ·  '));
          chipLabel.setColor('#dd99ff');
          chipBg.setStrokeStyle(1, 0xcc44ff, 0.95);
          chipBg.setSize(chipLabel.width + 56, 52);
        });
      }
    }

    const mute = this.addUi(this.add.text(width - 42, height - 60, this.sfx?.muted() ? t('sfx_off') : t('sfx_on'), {
      fontFamily: SANS, fontSize: '26px', color: '#8d98bd', fontStyle: '700',
      padding: { x: 24, y: 50 },
    }).setOrigin(1, 0.5).setScrollFactor(0).setInteractive({ useHandCursor: true }));
    mute.on('pointerdown', () => {
      const m = this.sfx.toggleMute();
      mute.setText(m ? t('sfx_off') : t('sfx_on'));
    });

    this.tapHint = this.addUi(this.add.text(width / 2, height - 60, t('hud_tap_to_breach'), {
      fontFamily: SANS, fontSize: '32px', color: '#88e8ff',
      fontStyle: '700',
    }).setOrigin(0.5).setScrollFactor(0));
    this.tweens.add({
      targets: this.tapHint,
      alpha: { from: 1, to: 0.4 },
      duration: 900,
      yoyo: true, repeat: -1,
    });

    // Gesture help lives on its own line above the bottom row, so it never
    // collides with the tap prompt.
    this.addUi(this.add.text(width / 2, height - 118, t('hud_drag_zoom_help'), {
      fontFamily: SANS, fontSize: '24px', color: '#56608a',
    }).setOrigin(0.5).setScrollFactor(0));
  }

  private buildTutorial() {
    const level = getLevel(this.levelId);
    if (!level) return;
    // Point the finger at a real breach point (leftmost tappable node), never
    // at a mechanic node the player isn't allowed to tap.
    const breachable = level.nodes.filter(isBreachable).sort((a, b) => a.x - b.x);
    const first = breachable[0] ?? [...level.nodes].sort((a, b) => a.x - b.x)[0];
    if (!first) return;

    // The floating panel ("TAP A NODE") and arrow are UI: pinned to viewport.
    // The finger ring stays in world-space so it lines up with the actual node.
    const { width } = this.scale;
    this.tutorial = this.add.container(width / 2, this.hudBottom + 100).setDepth(950).setScrollFactor(0);
    this.addUi(this.tutorial);
    this.cameras.main.ignore(this.tutorial);

    const panel = new Panel(this, -280, -66, 560, 132, {
      fill: 0x0b1226, fillAlpha: 0.97, stroke: 0x18e0ff, strokeAlpha: 0.9,
    }).gfx;
    const label = this.add.text(0, -22, t('tut_tap_any_node'), {
      fontFamily: SANS, fontSize: '38px', color: '#f2f6ff', fontStyle: '800',
    }).setOrigin(0.5);
    const sub = this.add.text(0, 28, t('tut_they_all_work'), {
      fontFamily: SANS, fontSize: '26px', color: '#8fb0ff',
    }).setOrigin(0.5);
    if (label.width > 520) label.setScale(520 / label.width);
    if (sub.width > 520) sub.setScale(520 / sub.width);
    const arrow = this.add.graphics();
    arrow.fillStyle(0x18e0ff, 0.95);
    arrow.fillTriangle(-20, 66, 20, 66, 0, 96);
    const bk = this.add.graphics();
    this.tutorial.add([panel, bk, label, sub, arrow]);

    const finger = this.add.graphics().setDepth(50);
    const fingerX = first.x;
    const fingerY = first.y;
    const drawFinger = (pulse: number) => {
      finger.clear();
      const r = 66 + pulse * 42;
      finger.lineStyle(4, 0x88e8ff, (1 - pulse) * 0.9);
      finger.strokeCircle(fingerX, fingerY, r);
      const r2 = 54 + pulse * 24;
      finger.lineStyle(2.5, 0x88e8ff, (1 - pulse) * 0.6);
      finger.strokeCircle(fingerX, fingerY, r2);
    };
    drawFinger(0);
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 1100,
      repeat: -1,
      onUpdate: (tw) => drawFinger(tw.getValue() ?? 0),
    });
    // Finger ring lives in WORLD space — keep it off the UI camera.
    if (this.uiCam) this.uiCam.ignore(finger);

    // On the tutorial level every node is a valid starter — pulse the rest
    // (excluding the finger node) so the player sees they can pick any.
    if (this.graph) {
      for (const node of this.graph.nodes.values()) {
        if (node.def.id === first.id) continue;
        if (!isBreachable(node.def)) continue;
        node.setBreachHighlight(this, true);
      }
    }

    this.tweens.add({
      targets: this.tutorial,
      y: this.tutorial.y - 6,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this._fingerGraphics = finger;
  }

  private _fingerGraphics?: Phaser.GameObjects.Graphics;

  private dismissTutorial() {
    if (this.tutorial) {
      this.tweens.add({
        targets: this.tutorial,
        alpha: 0,
        duration: 220,
        onComplete: () => this.tutorial?.destroy(),
      });
      this.tutorial = undefined;
    }
    if (this._fingerGraphics) {
      const f = this._fingerGraphics;
      this.tweens.add({
        targets: f,
        alpha: 0,
        duration: 220,
        onComplete: () => f.destroy(),
      });
      this._fingerGraphics = undefined;
    }
  }

  private onBreach(nodeId: string) {
    if (this.tapped) return;
    this.tapped = true;
    this.lastThinkMs = Date.now() - this.levelStartMs;
    Buzz.breach();

    this.tapHint?.destroy();
    this.tapHint = undefined;
    this.hintBtn?.setVisible(false);
    this.dismissTutorial();

    // Clear any tutorial-pulse highlights so they don't ghost into playback
    if (this.graph) {
      for (const node of this.graph.nodes.values()) {
        node.setBreachHighlight(this, false);
      }
    }

    const level = getLevel(this.levelId)!;
    const result = simulate(level, nodeId);

    // Skip affordance — built BEFORE inPlayback flips so routeNewObject leaves
    // it on the UI camera. A tap anywhere fast-forwards the cascade; a drag is
    // a camera pan and must NOT skip, so we gate on pointer travel distance.
    const skipHint = this.buildSkipHint();
    let downAt = { x: 0, y: 0 };
    const onDown = (p: Phaser.Input.Pointer) => { downAt = { x: p.x, y: p.y }; };
    const onUp = (p: Phaser.Input.Pointer) => {
      if (Phaser.Math.Distance.Between(downAt.x, downAt.y, p.x, p.y) < 14) {
        this.graph?.requestSkip();
      }
    };
    this.input.on('pointerdown', onDown);
    this.input.on('pointerup', onUp);

    this.inPlayback = true;
    this.graph?.playback(result, {
      onComplete: (r) => {
        this.inPlayback = false;
        this.input.off('pointerdown', onDown);
        this.input.off('pointerup', onUp);
        skipHint?.destroy();
        this.finish(r);
      },
    });
  }

  /** Pinned "tap to skip" hint shown during cascade playback. */
  private buildSkipHint(): Phaser.GameObjects.Text {
    const { width, height } = this.scale;
    const hint = this.add.text(width / 2, height - 54, t('hud_tap_to_skip'), {
      fontFamily: FONT_STACK, fontSize: '22px', color: '#88a8ff', fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(960);
    this.cameras.main.ignore(hint);
    this.tweens.add({
      targets: hint, alpha: { from: 0.45, to: 1 },
      duration: 700, yoyo: true, repeat: -1,
    });
    return hint;
  }

  // ── Endless HUD: lives + ad-hint ────────────────────────────────────────────
  private buildEndlessHud(_level: LevelDef) {
    const { width, height } = this.scale;

    this.livesText = this.addUi(this.add.text(width - 140, 58, this.livesLabel(), {
      fontFamily: SANS, fontSize: '30px', color: '#ff7a99', fontStyle: '700',
    }).setOrigin(1, 0.5).setScrollFactor(0).setDepth(905));

    this.addUi(this.add.text(width - 140, 104, `${fmtScore(Progress.runScore())} pts`, {
      fontFamily: SANS, fontSize: '26px', color: '#ffd070', fontStyle: '700',
    }).setOrigin(1, 0.5).setScrollFactor(0).setDepth(905));

    const c = this.addUi(this.add.container(width / 2, height - 200).setScrollFactor(0).setDepth(906));
    const bg = this.add.rectangle(0, 0, 300, 96, 0x0b1226, 0.96)
      .setStrokeStyle(2, 0xffd070, 0.9).setInteractive({ useHandCursor: true });
    const label = this.add.text(0, 0, `${t('hint_button')}  ▸ AD`, {
      fontFamily: SANS, fontSize: '30px', color: '#ffd070', fontStyle: '700',
    }).setOrigin(0.5);
    c.add([bg, label]);
    bg.on('pointerover', () => bg.setFillStyle(0x141a2e));
    bg.on('pointerout', () => bg.setFillStyle(0x0a1024));
    bg.on('pointerdown', () => { this.sfx?.ui(); void this.onHintPressed(); });
    this.hintBtn = c;
    // Nothing to rule out (only winner is breachable) → no point offering it.
    if (this.eliminableStarters().length === 0) c.setVisible(false);
  }

  private livesLabel(): string {
    const n = Progress.lives();
    return `${t('lives_label')} ${'●'.repeat(n)}${'○'.repeat(Math.max(0, MAX_LIVES - n))}`;
  }

  /** Breachable, losing, not-yet-ruled-out starters. Never includes a winner (see src/hint.ts). */
  private eliminableStarters(): string[] {
    if (!this.graph) return [];
    const level = getLevel(this.levelId)!;
    const out: string[] = [];
    for (const n of level.nodes) if (this.graph.nodes.get(n.id)?.isEliminated()) out.push(n.id);
    return hintCandidates(level, out);
  }

  private async onHintPressed() {
    if (this.tapped || this.hintBusy) return;
    if (this.eliminableStarters().length === 0) { this.toast(t('hint_none_left')); return; }
    this.hintBusy = true;
    try {
      const ok = await this.confirmHint();
      if (!ok) return;
      const watched = await Ads.rewarded(this);
      if (!watched) return;
      Progress.recordHint();
      this.hintUsedThisLevel = true;
      const elig = this.eliminableStarters();
      if (elig.length === 0) { this.toast(t('hint_none_left')); return; }
      const victim = elig[Math.floor(Math.random() * elig.length)];
      this.graph?.nodes.get(victim)?.markEliminated(this);
      this.sfx?.ui();
      this.toast(t('hint_removed'));
      if (this.eliminableStarters().length === 0) this.hintBtn?.setVisible(false);
    } finally {
      this.hintBusy = false;
    }
  }

  /** Lightweight yes/no modal for the hint offer. Resolves true on "watch". */
  private confirmHint(): Promise<boolean> {
    return new Promise((resolve) => {
      const { width, height } = this.scale;
      const root = this.add.container(0, 0).setScrollFactor(0).setDepth(2000);
      this.cameras.main.ignore(root);
      const dim = this.add.rectangle(0, 0, width, height, 0x000000, 0.8).setOrigin(0).setInteractive();
      const cardW = Math.min(width - 96, 880);
      const cardH = 780;
      const cx = width / 2, cy = height / 2;
      const card = new Panel(this, cx - cardW / 2, cy - cardH / 2, cardW, cardH, {
        fill: 0x0b1226, fillAlpha: 0.99, stroke: 0xffd070, strokeAlpha: 0.85,
      }).gfx;
      const title = this.add.text(cx, cy - cardH / 2 + 80, t('hint_confirm_title'), {
        fontFamily: SANS, fontSize: '48px', color: '#ffd070', fontStyle: '800',
        align: 'center', wordWrap: { width: cardW - 96 },
      }).setOrigin(0.5);
      const body = this.add.text(cx, cy - 70, t('hint_confirm_body'), {
        fontFamily: SANS, fontSize: '32px', color: '#cdd8f5',
        align: 'center', wordWrap: { width: cardW - 120 }, lineSpacing: 10,
      }).setOrigin(0.5);
      root.add([dim, card, title, body]);

      const done = (val: boolean) => { root.destroy(); resolve(val); };
      const yes = this.modalButton(cx, cy + 150, t('hint_yes'), 0x66ffaa, () => done(true), cardW - 120, true);
      const no = this.modalButton(cx, cy + 290, t('no_thanks'), 0x6a8cff, () => done(false), cardW - 120, false);
      root.add([yes, no]);
      dim.on('pointerdown', () => done(false));
      registerOverlay(() => !!root.scene && root.active, () => done(false));
    });
  }

  private modalButton(x: number, y: number, label: string, color: number, onClick: () => void, w = 360, primary = false): Phaser.GameObjects.Container {
    return uiButton(this, x, y, w, primary ? 120 : 104, clean(label), color, onClick, { primary, size: primary ? 36 : 30 });
  }

  /** Transient centred notice (UI camera). */
  private toast(msg: string) {
    const { width, height } = this.scale;
    const txt = this.add.text(width / 2, height * 0.34, msg, {
      fontFamily: SANS, fontSize: '32px', color: '#ffd070', fontStyle: '700',
      align: 'center', wordWrap: { width: width - 200 },
      backgroundColor: '#0b1226', padding: { x: 28, y: 20 },
    }).setOrigin(0.5).setScrollFactor(0).setDepth(2100);
    this.cameras.main.ignore(txt);
    this.tweens.add({
      targets: txt, alpha: { from: 0, to: 1 }, y: height * 0.32,
      duration: 220, ease: 'Cubic.easeOut',
      onComplete: () => this.tweens.add({
        targets: txt, alpha: 0, delay: 1400, duration: 400, onComplete: () => txt.destroy(),
      }),
    });
  }

  private finish(result: SimResult) {
    const wasSolved = Progress.isSolved(this.levelId);
    const endless = isProceduralId(this.levelId);
    const daily = isDailyId(this.levelId);
    let scoreGain: number | undefined;
    let runScore: number | undefined;
    if (result.outcome === 'win') {
      Progress.markSolved(this.levelId);
      Progress.recordWin(endless);
      if (endless) {
        const index = proceduralIndex(this.levelId);
        Progress.markEndlessCleared(index);
        const streak = Progress.bumpEndlessStreak();
        Progress.noteEndlessLevelDone();
        const level = getLevel(this.levelId)!;
        scoreGain = scoreLevel({ level, index, thinkMs: this.lastThinkMs, usedHint: this.hintUsedThisLevel, streak }).total;
        runScore = Progress.addRunScore(scoreGain);
      } else if (daily) {
        Progress.markDailySolved(dailyKeyFromId(this.levelId), dailyKeyYesterday());
      }
      this.sfx.win();
      Buzz.win();
    } else {
      Progress.recordFail(endless);
      if (endless) {
        Progress.resetEndlessStreak();
        Progress.loseLife();
        Progress.noteEndlessLevelDone();
      }
      this.sfx.lose();
      Buzz.fail();
    }

    this.scene.launch('Result', {
      levelId: this.levelId,
      result,
      isFirstClear: result.outcome === 'win' && !wasSolved,
      scoreGain,
      runScore,
    });
    this.scene.bringToTop('Result');
  }

  /** Routes objects created during playback out of the UI camera. */
  private routeNewObject(obj: Phaser.GameObjects.GameObject) {
    if (this.inPlayback && this.uiCam) this.uiCam.ignore(obj);
  }

  private onShutdown() {
    // Clear our custom scene-event listeners so they don't accumulate across
    // level restarts (Scene.events, unlike the input plugin, is NOT auto-cleared).
    this.events.removeAllListeners('nodefall:required-collapsed');
    this.events.removeAllListeners('nodefall:core-collapsed');
    this.events.off(Phaser.Scenes.Events.ADDED_TO_SCENE, this.routeNewObject, this);
    // Restore tween timescale in case a hit-stop was active when shutdown ran.
    this.tweens.timeScale = 1;
    this.graph?.destroy();
    this.graph = undefined;
    this.rig?.destroy();
    this.rig = undefined;
    this.legend?.destroy();
    this.legend = undefined;
    if (this.uiCam) {
      this.cameras.remove(this.uiCam);
      this.uiCam = undefined;
    }
  }
}
